import {
  Alert,
  Chip,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import React from "react";
import { LayerEditor } from "components/common/plan/layerEditor/LayerEditor";
import {
  basketsToMixCreate,
  savedMixToLayerState,
} from "components/common/plan/layerEditor/conversions";
import { SteelGradeChip } from "components/common/plan/SteelGradeChip";
import {
  ChildMixUpdate,
  MaterialRead,
  MixCreate,
  ProductionMixResultRead,
} from "src/store/api/generatedApi";

import { basketsEqual } from "components/common/plan/utils";
import { LayeringPreview, useLayeringPreview } from "./useLayeringPreview";

interface Props {
  open: boolean;
  mixResult: ProductionMixResultRead | null;
  materials: MaterialRead[];
  onApply: (update: ChildMixUpdate) => void;
  onClose: () => void;
  previewLayering?: LayeringPreview;
}

export const MixLayeringDialog = ({
  open,
  mixResult,
  materials,
  onApply,
  onClose,
  previewLayering,
}: Props) => {
  const { t } = useTenantTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {mixResult !== null ? (
          <Stack gap={1}>
            <Typography variant="h5" component="span">
              {t("mix")}: {mixResult.mix.id}
            </Typography>
            <Stack
              direction="row"
              gap={0.5}
              sx={{ overflowX: "auto", pb: 0.5 }}
            >
              {mixResult.production.map((item) => (
                <Box key={item.steel_grade_name} sx={{ flexShrink: 0 }}>
                  <SteelGradeChip
                    label={item.steel_grade_name}
                    selected={false}
                    focused={false}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        ) : (
          t("editLayers")
        )}
      </DialogTitle>
      {open && mixResult !== null ? (
        <MixLayeringDialogBody
          key={mixResult.mix.id}
          mixResult={mixResult}
          materials={materials}
          previewLayering={previewLayering}
          onApply={onApply}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
};

const savedMixBaskets = (mixResult: ProductionMixResultRead) =>
  basketsToMixCreate(savedMixToLayerState(mixResult).baskets).baskets;

interface BodyProps {
  mixResult: ProductionMixResultRead;
  materials: MaterialRead[];
  onApply: (update: ChildMixUpdate) => void;
  onClose: () => void;
  previewLayering?: LayeringPreview;
}

const MixLayeringDialogBody = ({
  mixResult,
  materials,
  onApply,
  onClose,
  previewLayering,
}: BodyProps) => {
  const { t } = useTenantTranslation();
  const { small_mass_per_mass, default_mix_breakdown } = useTenantData();
  const baseMassScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;

  const originalBaskets = React.useMemo(
    () => savedMixBaskets(mixResult),
    [mixResult]
  );
  const [editorMix, setEditorMix] = React.useState(mixResult);
  const [resetVersion, setResetVersion] = React.useState(0);
  const [draftMix, setDraftMix] = React.useState<MixCreate>(mixResult.mix);
  const [syncedMixResult, setSyncedMixResult] = React.useState(mixResult);
  if (syncedMixResult !== mixResult) {
    setSyncedMixResult(mixResult);
    if (basketsEqual(draftMix.baskets, savedMixBaskets(syncedMixResult))) {
      setEditorMix(mixResult);
      setDraftMix(mixResult.mix);
    }
  }
  const initialState = React.useMemo(
    () => [savedMixToLayerState(editorMix)],
    [editorMix]
  );
  const { automaticMix, isChecking, hasError, retry } = useLayeringPreview({
    mix: draftMix,
    preview: previewLayering,
  });
  const hasEdits = !basketsEqual(draftMix.baskets, originalBaskets);
  const matchesAutomatic =
    automaticMix !== null &&
    basketsEqual(draftMix.baskets, automaticMix.baskets);
  const handleStateChange = React.useCallback((updates: ChildMixUpdate[]) => {
    const update = updates[0] ?? null;
    const baskets = update?.new_mix_production.mix?.baskets;
    if (baskets !== undefined)
      setDraftMix((current) =>
        basketsEqual(current.baskets, baskets)
          ? current
          : { ...current, baskets }
      );
  }, []);
  const applyAutomaticLayers = () => {
    if (automaticMix === null) return;
    const baskets = [
      ...automaticMix.baskets,
      ...editorMix.mix.baskets
        .filter(
          (basket) =>
            !automaticMix.baskets.some(
              (automaticBasket) =>
                automaticBasket.basket_number === basket.basket_number
            )
        )
        .map((basket) => ({ ...basket, layers: [] })),
    ];
    setResetVersion((version) => version + 1);
    setDraftMix((current) => ({ ...current, baskets }));
    setEditorMix((current) => ({
      ...current,
      mix: { ...current.mix, baskets },
    }));
  };

  const handleApply = () => {
    onApply({
      parent_mix_ids: [mixResult.mix.id],
      new_mix_production: {
        name: mixResult.name,
        description: mixResult.description ?? null,
        production: mixResult.production,
        mix: draftMix,
      },
    });
    onClose();
  };

  return (
    <>
      <DialogContent sx={{ minHeight: 480 }}>
        {previewLayering !== undefined ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {isChecking ? (
                <>
                  <CircularProgress size={16} />
                  <Typography role="status">{t("checkingLayering")}</Typography>
                </>
              ) : automaticMix !== null ? (
                <Chip
                  label={t(
                    matchesAutomatic
                      ? "automaticallyLayered"
                      : "manuallyLayered"
                  )}
                  size="small"
                  color={matchesAutomatic ? "default" : "warning"}
                />
              ) : null}
              <Button
                onClick={applyAutomaticLayers}
                disabled={automaticMix === null || matchesAutomatic}
              >
                {t("resetToAutoLayering")}
              </Button>
            </Stack>
            {hasError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" onClick={retry}>
                    {t("retry")}
                  </Button>
                }
              >
                {t("errorAutomaticLayering")}
              </Alert>
            ) : null}
          </Stack>
        ) : null}
        <LayerEditor
          initialState={initialState}
          resetVersion={resetVersion}
          materials={materials}
          onStateChange={handleStateChange}
          weightsEditable
          massScaleFactor={baseMassScaleFactor}
          size="comfortable"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="text">
          {t("cancel")}
        </Button>
        <Button
          data-testid="apply-layer-edits"
          onClick={handleApply}
          disabled={!hasEdits}
          color="primary"
          variant="contained"
        >
          {t("applyLayerEdits")}
        </Button>
      </DialogActions>
    </>
  );
};
